/** 시설 motion 위험 감지 기본값 (백엔드 DEFAULT_POOL_RISK_MOTION과 동기) */



export interface PoolRiskMotionSubtypeScores {

  drowning: number;

  no_motion: number;

  sinking: number;

}



export interface PoolRiskMotionDefaults {

  sensitivity: number;

  stillness_threshold_sec: number;

  combined_score_threshold: number;

  legacy_detection_threshold: number;

  subtype_min_score: PoolRiskMotionSubtypeScores;

}



export const DEFAULT_POOL_RISK_MOTION: PoolRiskMotionDefaults = {

  sensitivity: 0.72,

  stillness_threshold_sec: 8,

  combined_score_threshold: 28,

  legacy_detection_threshold: 3.4,

  subtype_min_score: {

    drowning: 0.32,

    no_motion: 0.38,

    sinking: 0.3,

  },

};



export function mergePoolRiskMotionDefaults(

  partial?: Partial<PoolRiskMotionDefaults> | null

): PoolRiskMotionDefaults {

  const p = partial || {};

  const d = DEFAULT_POOL_RISK_MOTION;

  const num = (v: unknown, fallback: number) => {

    const n = Number(v);

    return Number.isFinite(n) ? n : fallback;

  };

  const sub: Partial<PoolRiskMotionSubtypeScores> = p.subtype_min_score ?? {};

  return {

    sensitivity: Math.min(1, Math.max(0, num(p.sensitivity, d.sensitivity))),

    stillness_threshold_sec: Math.max(4, num(p.stillness_threshold_sec, d.stillness_threshold_sec)),

    combined_score_threshold: Math.min(60, Math.max(12, num(p.combined_score_threshold, d.combined_score_threshold))),

    legacy_detection_threshold: Math.min(8, Math.max(1.5, num(p.legacy_detection_threshold, d.legacy_detection_threshold))),

    subtype_min_score: {

      drowning: Math.min(0.9, Math.max(0.1, num(sub.drowning, d.subtype_min_score.drowning))),

      no_motion: Math.min(0.9, Math.max(0.1, num(sub.no_motion, d.subtype_min_score.no_motion))),

      sinking: Math.min(0.9, Math.max(0.1, num(sub.sinking, d.subtype_min_score.sinking))),

    },

  };

}



/** MUI helperText 줄바꿈용 */

export const POOL_RISK_MOTION_HELPER_PROPS = {

  sx: { whiteSpace: 'pre-line' as const },

};



export const POOL_RISK_MOTION_SECTION_INTRO =

  '영상에서 잡힌 사람의 움직임 궤적을 보고 「익사」「움직임 없음」「가라앉음」 위험을 추정. ' +

  '여기는 이 시설 전체에 공통으로 쓰는 숫자. CCTV 관리에서 「시설 motion 기본값 사용」을 켠 카메라는 이 값을 따르고, ' +

  '물이 보이는 구역(사각형)만 카메라마다 다르게 그림. ' +

  '관제 화면에서 AI 감지를 켜고, 시스템 설정의 「서버 motion·위험 평가」도 켜져 있어야 동작.';



export const POOL_RISK_MOTION_CCTV_SECTION_NOTE =
  '통합 점수·유형(익사/움직임 없음/가라앉음) 임계는 「시설 등록 → Motion 위험 감지」에서 설정. ' +
  '여기서는 카메라별 물 구역과, 필요할 때만 이 카메라 전용 민감도·정지 시간을 조정.';

/** CCTV 폼용 — 시설 폼의 전체 가이드 대신 짧은 요약 */
export const POOL_RISK_MOTION_CCTV_TUNING_SUMMARY =
  '【요약】오탐이 많으면 민감도를 조금 내리거나, 시설 화면의 통합 점수 임계를 올림. ' +
  '이 카메라만 다르면 「시설 motion 기본값 사용」을 끄고 아래 민감도·정지만 조정. ' +
  '자세한 순서·용어는 시설 등록의 Motion 위험 감지 안내를 참고.';

export const POOL_RISK_MOTION_TUNING_GUIDE =

  '【처음 쓸 때】아래 기본값(민감도 0.72, 정지 8초, 통합 28 등)으로 1~2일간 관제하며 확인.\n' +

  '【오탐이 많을 때】① 민감도를 0.05~0.1 낮춤 → ② 통합 점수 임계를 2~4 올림 → ③ 정상 수영이 「움직임 없음」으로 뜨면 정지 기준(초)을 1~2 늘림. 빠른 수영 오탐은 주로 ①·②로 줄.\n' +

  '【놓치는 경우가 걱정될 때】민감도·통합 점수 임계를 조금씩만 조정. 한 번에 크게 바꾸지 않음.\n' +

  '【카메라마다 다를 때】시설 값은 유지한 채, CCTV에서 스위치를 끄고 그 카메라만 민감도·정지를 조정.\n' +

  '【용어】「통합 점수」= 여러 신호를 합친 0~100 위험 점수. 「유형 하한」= 관제에 표시할 위험 종류(익사 등)로 나누기 위한 최소 패턴 강도(0~1).';



export const POOL_RISK_MOTION_HINTS = {

  sensitivity:

    '【의미】이 시설의 motion 위험 점수 전체에 곱하는 배율(0~1). 같은 상황이라도 숫자가 크면 경보가 더 쉽게 뜸.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.sensitivity}】대부분 시설의 시작값.\n` +

    '【올릴 때】의심 상황을 더 빨리 잡을 때. 오탐이 늘면 다시 내림.\n' +

    '【낮출 때】수영·물놀이 중 경보가 자주 발생할 때. 0.65 전후부터 소폭씩 조정.',

  stillness:

    '【의미】「거의 움직이지 않음」「부유」처럼 보이는 상태가 이 시간(초)에 가까워질수록 「움직임 없음」 쪽 위험 가중이 커짐.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.stillness_threshold_sec}초】짧은 멈춤과 긴 정지를 구분하는 기준.\n` +

    '【늘릴 때】잠깐 멈춘 수영자까지 경보가 나면 9~12초로 늘림.\n' +

    '【줄일 때】오래 가만히 있는 경우를 더 빨리 보려면 6~7초로 줄이기 가능(오탐 주의).',

  combined:

    '【의미】여러 motion 신호를 합친 「통합 위험 점수」(대략 0~100)가 이 숫자 이상이면 위험 후보로 올림. 관제 빨간 박스·알림의 첫 관문.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.combined_score_threshold}】낮을수록 민감, 높을수록 보수적.\n` +

    '【올릴 때】오탐이 많으면 30~35까지 올림.\n' +

    '【낮출 때】경보가 너무 늦게 뜨면 24~26 전후로 낮추기 가능.',

  legacy:

    '【의미】예전 방식의 「익사·격렬 움직임」 패턴 점수가 이 값 이상일 때만 그 패턴으로 위험을 인정. 통합 점수와 함께 쓰.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.legacy_detection_threshold}】전문가가 아니어도 기본값 유지를 권장.\n` +

    '【올릴 때】격렬한 수영·뛰어들기가 익사로 잡히면 3.6~4.0으로 올림.\n' +

    '【낮출 때】특정 패턴만 놓친다고 판단될 때만 소폭 내림.',

  subtypeDrowning:

    '【의미】관제 메시지를 「익사」로 붙이기 위한 최소 패턴 강도(0~1). 통합 점수로 위험은 잡혔는데 종류만 나눌 때 사용.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.subtype_min_score.drowning}】\n` +

    '【올릴 때】다른 유형으로만 뜨고 익사 라벨이 거의 없을 때만 소폭 올림.\n' +

    '【낮출 때】익사로 분류되는 경우가 과하면 낮춤.',

  subtypeNoMotion:

    '【의미】「움직임 없음」 라벨을 붙이기 위한 최소 패턴 강도(0~1).\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.subtype_min_score.no_motion}】정지·부유 판정과 함께 동작.\n` +

    '【올릴 때】가만히 있는 사람이 다른 유형으로만 표시될 때.\n' +

    '【낮출 때】짧은 정지까지 「움직임 없음」으로만 뜰 때.',

  subtypeSinking:

    '【의미】「가라앉음」 라벨을 붙이기 위한 최소 패턴 강도(0~1). 수면 아래로 내려가는 움직임 패턴에 가까움.\n' +

    `【기본 ${DEFAULT_POOL_RISK_MOTION.subtype_min_score.sinking}】\n` +

    '【올릴 때】가라앉음으로 보이는데 다른 유형만 뜰 때만 조정.\n' +

    '【낮출 때】다이빙·잠수 후 오탐이 많을 때.',

  cctvUsePool:

    '【켜짐(권장)】이 CCTV는 시설 등록 화면의 motion 기본값(민감도·정지·통합·유형 임계)을 그대로 사용. 시설 단위로 맞추기 쉬움.\n' +

    '【꺼짐】이 카메라만 아래 「민감도」「정지 기준 시간」을 따로 입력. 각도·줌·조명이 다른 카메라에 유용.\n' +

    '【항상 CCTV별】물(감시) 구역 사각형 — 미설정이면 위험 분석이 약해짐.',

  cctvSensitivity:

    '【의미】시설 민감도와 같은 개념이지만 이 카메라에만 적용됨(스위치를 꺼야 입력 가능).\n' +

    '【비우기】스위치를 켜 두면 시설 값이 적용되므로 여기는 비활성.\n' +

    '【조정】오탐 많음 → 낮춤 / 놓침 걱정 → 소폭 올림. 한 번에 0.05 단위 권장.',

  cctvStillness:

    '【의미】이 카메라 영상 기준 「움직임 없음」 가중에 쓰는 초 단위(스위치를 꺼야 입력 가능).\n' +

    '【늘릴 때】이 각도에서 짧은 멈춤까지 경보가 나올 때.\n' +

    '【줄일 때】이 카메라에서만 오래 정지한 경우를 더 빨리 보려 할 때.',

  cctvZones:

    '【의미】물이 보이는(또는 AI가 볼) 영역만 사각형으로 지정. 구역이 있으면 그 안만 YOLO 추론하고, 밖은 bbox·위험 알림에서 빠짐.\n' +

    '【방법】아래 편집기에서 영상 위를 드래그하거나, 고급 JSON을 직접 수정. 원거리는 박스 밖으로 빼 둠.',

  monitoringEvaluateMotion:

    '【의미】중앙 관제 서버가 YOLO 박스뿐 아니라 궤적·정지·패턴으로 위험을 평가할지 여부.\n' +

    '【켜짐(권장)】익사·움직임 없음·가라앉음 알림과 빨간 위험 박스에 필요.\n' +

    '【꺼짐】박스만 빠르게 보고 싶을 때. 민감도 숫자는 시설/CCTV 설정과 별개.\n' +

    '【민감도 조정】시스템 관리 → 시설 등록의 「Motion 위험 감지」 또는 CCTV 「위험 감지」에서.',

} as const;


